import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../utils';

/**
 * A team that exists only because of an online payment that failed waits for that payment:
 * its actions are greyed out and this banner offers "Pay now". The API refuses the actions
 * too (402 'teamNotPaid'), so this is the explanation, not the lock itself.
 *
 * useTeamPaymentLock({ teamId, ownTeam, enabled }) — teamId sends it as Otherteamid (a
 * team you view or manage); ownTeam asks for the team on your login and ignores a leftover
 * otherTeamId; neither = whatever the page's session headers say (EditTeam, Players).
 * Returns null while loading or when nothing is locked.
 */
export function useTeamPaymentLock({ teamId = null, ownTeam = false, enabled = true } = {}) {
  const [lock, setLock] = useState(null);
  useEffect(() => {
    if (!enabled) { setLock(null); return; }
    let alive = true;
    const url = ownTeam ? '/team-payment-lock?own_team=1' : '/team-payment-lock';
    apiRequest('GET', url, null, teamId ? { Otherteamid: teamId } : {})
      .then((d) => { if (alive) setLock(d?.lock || null); })
      .catch(() => { if (alive) setLock(null); });
    return () => { alive = false; };
  }, [teamId, ownTeam, enabled]);
  return lock;
}

export default function TeamPaymentBanner({ lock, className = '' }) {
  const { t } = useTranslation();
  if (!lock) return null;
  return (
    <div className={`alert alert-warning d-flex align-items-center gap-2 flex-wrap ${className}`} role='alert'>
      <div className='flex-grow-1'>
        <strong>{t('teamNotPaidTitle')}</strong>
        <div className='small'>{t('teamNotPaidSub', { amount: lock.amount })}</div>
      </div>
      {/* /api/pay mints a fresh Stripe session, or shows the receipt when it is paid already. */}
      <a className='btn btn-sm btn-primary' href={`/api/pay/${lock.order_id}`}>{t('payNow')}</a>
    </div>
  );
}
